/**
 * Слои генплана поверх подложки.
 *
 * Тип геометрии заранее неизвестен: в одном и том же слое ФГИС ТП могут лежать
 * и полигоны зон, и линии (красные линии, границы), и точки (объекты
 * инфраструктуры). Поэтому на каждый слой источника создаётся полный комплект
 * отрисовщиков с фильтром по `geometry-type` — вместо того чтобы угадывать.
 */

import type { ExpressionSpecification, LayerSpecification } from "@maplibre/maplibre-gl-style-spec";
import { isRaster, type SourceLayerInfo, type TileSourceInfo } from "~/config/manifest";
import { ZONE_CODE_FIELDS, zoneFillColorExpression } from "~/config/zones";

/** Палитра для слоёв, не являющихся функциональным зонированием. */
const ACCENT_COLORS = ["#d94f4f", "#3f7fbf", "#c98b2e", "#57a457", "#8a63c4", "#3fa6a0"];

export interface ThematicLayerGroup {
  /** Идентификатор, пригодный для UI и URL: <источник>__<слой> */
  key: string;
  sourceId: string;
  sourceLayer: string;
  title: string;
  /** Растровый слой — картинка чертежа; кликать и красить по коду нечего. */
  raster: boolean;
  /**
   * Оговорка о происхождении слоя для панели слоёв.
   *
   * У растровых листов она обязательна и у разных листов разная: лист,
   * привязанный нашим подбором по воде, и лист, пришедший уже привязанным с
   * геопортала администрации, отличаются по точности на два порядка. Одна
   * общая формулировка на оба случая была бы неправдой про один из них.
   */
  note: string | null;
  /** Показывать ли слой при первом открытии карты. */
  defaultVisible: boolean;
  /** Слой раскрашен по палитре функциональных зон. */
  isZoning: boolean;
  accentColor: string;
  fillLayerId: string;
  outlineLayerId: string;
  lineLayerId: string;
  pointLayerId: string;
  highlightFillId: string;
  highlightLineId: string;
  /** Все идентификаторы слоёв группы — для массового скрытия/показа. */
  layerIds: string[];
  /** Слои, по которым работает клик и наведение. */
  interactiveLayerIds: string[];
}

/**
 * Оговорки к растровым слоям: откуда лист и чего от него ждать.
 *
 * Ключ — имя источника, то есть имя архива в manifest.json. Слой, которого
 * здесь нет, получает осторожную формулировку по умолчанию: лучше сказать
 * «происхождение неизвестно», чем приписать чужому листу чью-то точность.
 */
/**
 * Порядок слоёв в панели.
 *
 * Без него список идёт по имени файла архива, то есть по алфавиту транслита, —
 * и основной лист функциональных зон оказывается в самом низу, под выключенными.
 * Здесь листы выстроены по тому, как их обычно смотрят: сначала назначение
 * территорий и ограничения, затем существующее положение и охрана, в конце
 * транспорт.
 *
 * Лист, которого здесь нет, попадает в конец списка, но не теряется.
 */
export const LAYER_ORDER = [
  "genplan_zones_072024",
  "genplan_zones_2024",
  "genplan_restrictions",
  "genplan_local_objects",
  "genplan_current_use",
  "genplan_settlement_boundary",
  "genplan_heritage",
  "genplan_social_infra",
  "genplan_sport_recreation",
  "genplan_transport_complex",
  "genplan_highways",
  "genplan_public_transport",
  "genplan_roads_parking",
];

const RASTER_NOTES: Record<string, string> = {
  genplan_zones_072024:
    "Назначение территорий: жилая застройка по этажности, общественно-деловые "
    + "и производственные зоны, транспортная и инженерная инфраструктура, "
    + "озеленённые территории, садоводства, сельхозугодья.",
  genplan_zones_2024:
    "Назначение территорий: жилая застройка по этажности, общественно-деловые "
    + "и производственные зоны, транспортная и инженерная инфраструктура, "
    + "озеленённые территории, садоводства, сельхозугодья.",
  genplan_settlement_boundary:
    "Черта города Калининграда и граница городского округа.",
  genplan_sport_recreation:
    "Планируемые объекты физической культуры и массового спорта, места "
    + "массового отдыха жителей.",
  genplan_restrictions:
    "Где нельзя или ограничено строить: санитарно-защитные зоны, охранные зоны "
    + "газопроводов, электросетей, связи и теплосетей, водоохранные и "
    + "прибрежные полосы, пояса санитарной охраны водозаборов, приаэродромная "
    + "территория, зоны затопления, особо охраняемые природные территории, "
    + "месторождения полезных ископаемых.",
  genplan_current_use:
    "Как территория используется сейчас. Единственный лист про существующее "
    + "положение — остальные показывают проектные решения.",
  genplan_local_objects:
    "Всё вместе: функциональные зоны и все планируемые объекты местного "
    + "значения на одном листе.",
  genplan_heritage:
    "Объекты культурного наследия, их территории и зоны охраны, особо "
    + "охраняемые природные территории.",
  genplan_transport_complex:
    "Развитие транспорта: дороги по категориям, развязки, объекты транспортной "
    + "инфраструктуры.",
  genplan_highways:
    "Городские магистрали и улично-дорожная сеть, включая проектируемую "
    + "окружную дорогу.",
  genplan_public_transport:
    "Маршруты общественного транспорта и транспортно-пересадочные узлы.",
  genplan_roads_parking:
    "Автомобильные дороги местного значения и планируемые парковки.",
  genplan_social_infra:
    "Объекты социальной инфраструктуры: образование, здравоохранение, культура "
    + "— с радиусами доступности.",
};


/**
 * Слои, которые публикуются, но при первом открытии выключены.
 *
 * Лист автопривязки — тот же документ, что и лист с геопортала, только
 * посаженный на координаты подбором и потому уезжающий на 50–150 метров.
 * Показывать оба разом бессмысленно: два полупрозрачных чертежа поверх друг
 * друга дают кашу, в которой не читается ни один. Слой оставлен ради сравнения
 * шторкой двух редакций документа, а такое сравнение включают осознанно.
 *
 * Ссылка с параметром layers по-прежнему главнее: если слой явно назван в
 * адресе, он включается, кто бы что ни решил по умолчанию.
 */
const HIDDEN_BY_DEFAULT = new Set([
  // Привязан автоподбором и смещён на 1,7–2,9 км. Заменён на genplan_zones_072024.
  "genplan_zones_raster",
  "genplan_settlement_boundary",
  "genplan_sport_recreation",
  // Вся серия группы B: девять тематических листов, включать их по умолчанию
  // означало бы девять чертежей поверх друг друга.
  "genplan_restrictions",
  "genplan_current_use",
  "genplan_local_objects",
  "genplan_heritage",
  "genplan_transport_complex",
  "genplan_highways",
  "genplan_public_transport",
  "genplan_roads_parking",
  "genplan_social_infra",
]);

const DEFAULT_RASTER_NOTE = "Лист генерального плана городского округа.";

const IS_POLYGON: ExpressionSpecification = ["==", ["geometry-type"], "Polygon"];
const IS_LINE: ExpressionSpecification = ["==", ["geometry-type"], "LineString"];
const IS_POINT: ExpressionSpecification = ["==", ["geometry-type"], "Point"];

function looksLikeZoning(layer: SourceLayerInfo): boolean {
  return layer.fields.some((field) => (ZONE_CODE_FIELDS as readonly string[]).includes(field));
}

function humanTitle(sourceId: string, layerId: string): string {
  const known: Record<string, string> = {
    genplan_zones_2024: "Функциональные зоны генплана (ред. 2024)",
    genplan_zones_072024: "Функциональные зоны (лист 07.2024)",
    genplan_settlement_boundary: "Границы населённого пункта",
    genplan_sport_recreation: "Спорт и места массового отдыха",
    genplan_restrictions: "Ограничения градостроительной деятельности",
    genplan_current_use: "Современное использование территории",
    genplan_local_objects: "Сводная карта объектов местного значения",
    genplan_heritage: "Культурное наследие и ООПТ",
    genplan_transport_complex: "Развитие транспортного комплекса",
    genplan_highways: "Городские магистрали и улично-дорожная сеть",
    genplan_public_transport: "Общественный транспорт и ТПУ",
    genplan_roads_parking: "Автодороги местного значения и парковки",
    genplan_social_infra: "Социальная инфраструктура",
    genplan_zones_raster: "Функциональные зоны (лист генплана)",
    functional_zones: "Функциональные зоны",
    red_lines: "Красные линии",
    boundaries: "Границы",
    infrastructure: "Объекты инфраструктуры",
    cadastre: "Кадастровые участки",
    zones_draft: "Проект изменений: зоны",
  };
  return known[layerId] ?? known[sourceId] ?? layerId.replace(/_/g, " ");
}

export interface BuildOptions {
  sourceId: string;
  source: TileSourceInfo;
  /** Порядковый номер источника — влияет только на выбор акцентного цвета. */
  index: number;
}

export function buildThematicGroups(options: BuildOptions): ThematicLayerGroup[] {
  const { sourceId, source, index } = options;

  if (isRaster(source)) {
    const key = `${sourceId}__raster`;
    const group: ThematicLayerGroup = {
      key,
      sourceId,
      sourceLayer: "",
      title: humanTitle(sourceId, sourceId),
      raster: true,
      note: RASTER_NOTES[sourceId] ?? DEFAULT_RASTER_NOTE,
      defaultVisible: !HIDDEN_BY_DEFAULT.has(sourceId),
      isZoning: false,
      accentColor: "#7a7f87",
      fillLayerId: `${key}--raster`,
      outlineLayerId: "",
      lineLayerId: "",
      pointLayerId: "",
      highlightFillId: "",
      highlightLineId: "",
      layerIds: [`${key}--raster`],
      interactiveLayerIds: [],
    };
    return [group];
  }

  return source.sourceLayers.map((sourceLayer, layerIndex) => {
    const key = `${sourceId}__${sourceLayer.id}`;
    const accent = ACCENT_COLORS[(index + layerIndex) % ACCENT_COLORS.length] as string;
    const isZoning = looksLikeZoning(sourceLayer);

    const group: ThematicLayerGroup = {
      key,
      sourceId,
      sourceLayer: sourceLayer.id,
      title: humanTitle(sourceId, sourceLayer.id),
      raster: false,
      note: null,
      defaultVisible: !HIDDEN_BY_DEFAULT.has(sourceId),
      isZoning,
      accentColor: isZoning ? "#ef7f2f" : accent,
      fillLayerId: `${key}--fill`,
      outlineLayerId: `${key}--outline`,
      lineLayerId: `${key}--line`,
      pointLayerId: `${key}--point`,
      highlightFillId: `${key}--highlight-fill`,
      highlightLineId: `${key}--highlight-line`,
      layerIds: [],
      interactiveLayerIds: [],
    };

    group.layerIds = [
      group.fillLayerId,
      group.outlineLayerId,
      group.lineLayerId,
      group.pointLayerId,
      group.highlightFillId,
      group.highlightLineId,
    ];
    group.interactiveLayerIds = [group.fillLayerId, group.lineLayerId, group.pointLayerId];

    return group;
  });
}

export function groupLayerSpecs(
  group: ThematicLayerGroup,
  opacity: number,
): LayerSpecification[] {
  const { sourceId, sourceLayer } = group;

  if (group.raster) {
    return [
      {
        id: group.fillLayerId,
        type: "raster",
        source: sourceId,
        paint: {
          "raster-opacity": opacity,
          // Чертёж свёрстан для печати: без сглаживания при промежуточных
          // зумах тонкие линии и подписи распадаются на лесенку.
          "raster-resampling": "linear",
        },
      },
    ];
  }
  const fillColor: string | ExpressionSpecification = group.isZoning
    ? zoneFillColorExpression()
    : group.accentColor;

  return [
    {
      id: group.fillLayerId,
      type: "fill",
      source: sourceId,
      "source-layer": sourceLayer,
      filter: IS_POLYGON,
      paint: {
        "fill-color": fillColor,
        "fill-opacity": opacity,
      },
    },
    {
      id: group.outlineLayerId,
      type: "line",
      source: sourceId,
      "source-layer": sourceLayer,
      filter: IS_POLYGON,
      paint: {
        "line-color": "#ffffff",
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.3, 16, 1.2],
        "line-opacity": Math.min(1, opacity + 0.2),
      },
    },
    {
      id: group.lineLayerId,
      type: "line",
      source: sourceId,
      "source-layer": sourceLayer,
      filter: IS_LINE,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": group.accentColor,
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 1, 18, 4],
        "line-opacity": Math.min(1, opacity + 0.25),
      },
    },
    {
      id: group.pointLayerId,
      type: "circle",
      source: sourceId,
      "source-layer": sourceLayer,
      filter: IS_POINT,
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 2.5, 17, 7],
        "circle-color": group.accentColor,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1,
        "circle-opacity": Math.min(1, opacity + 0.25),
      },
    },
    // Подсветка выбранного объекта. Отдельные слои с фильтром по feature-state
    // дешевле, чем перерисовка источника: MapLibre меняет только состояние
    // конкретного объекта и не трогает уже загруженные тайлы.
    {
      id: group.highlightFillId,
      type: "fill",
      source: sourceId,
      "source-layer": sourceLayer,
      filter: IS_POLYGON,
      paint: {
        "fill-color": "#1b6ef3",
        "fill-opacity": ["case", ["boolean", ["feature-state", "selected"], false], 0.25, 0],
      },
    },
    {
      id: group.highlightLineId,
      type: "line",
      source: sourceId,
      "source-layer": sourceLayer,
      // Контур подсветки нужен и полигонам, и линиям, но не точкам:
      // для точек line-слой всё равно ничего не рисует, зато тесселирует их впустую.
      filter: ["!=", ["geometry-type"], "Point"],
      paint: {
        "line-color": "#1b6ef3",
        "line-width": ["case", ["boolean", ["feature-state", "selected"], false], 2.5, 0],
      },
    },
  ];
}
